import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../component/context/AuthContext";
import { FaCalendarAlt, FaCashRegister, FaBoxOpen, FaUsers, FaPlus, FaArrowRight, FaExclamationTriangle, FaClock, FaChartLine, FaCut, FaUserPlus } from "react-icons/fa";
import "../styles/Dashboard.css";

axios.defaults.withCredentials = true;
const money = value => `₦${Number(value || 0).toLocaleString()}`;

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({netRevenue:0,shopShare:0,productTotal:0,totalBookings:0,activeStaff:0,avgSpend:0});
  const [liveBookings, setLiveBookings] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const DAILY_TARGET = 200000;

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, bookingsRes, activityRes, alertsRes] = await Promise.all([
        axios.get('/api/dashboard/stats'), axios.get('/api/dashboard/live-bookings'),
        axios.get('/api/dashboard/recent-activity'), axios.get('/api/inventory/low-stock')
      ]);
      setStats(statsRes.data || {}); setLiveBookings(bookingsRes.data || []);
      setRecentActivity(activityRes.data || []); setInventoryAlerts(alertsRes.data || []);
    } catch (err) { console.error("Dashboard refresh failed:", err.response?.status, err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDashboardData(); const timer=setInterval(fetchDashboardData,120000); return()=>clearInterval(timer); }, [fetchDashboardData]);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const today = now.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'});
  const displayName = user?.username || (user?.role === 'super_admin' ? 'Super Admin' : 'Admin');
  const progress = Math.min(((stats.netRevenue || 0) / DAILY_TARGET) * 100,100);

  const cards = [
    {label:"Revenue",value:money(stats.netRevenue),meta:"Service + product income",icon:FaChartLine,tone:"green"},
    {label:"Bookings",value:stats.totalBookings || 0,meta:"Appointments today",icon:FaCalendarAlt,tone:"purple"},
    {label:"Active Staff",value:stats.activeStaff || 0,meta:"Team available",icon:FaUsers,tone:"teal"},
    {label:"Product Sales",value:money(stats.productTotal),meta:"Retail sales",icon:FaBoxOpen,tone:"gold"},
  ];

  return <div className="salon-dashboard">
    <section className="dash-welcome">
      <div><span className="eyebrow">DEY FRESH SIGNATURE · SALON MANAGEMENT</span><h1>{greeting}, <em>{displayName}</em></h1><p>Here’s your salon overview for {today}.</p></div>
      <div className="welcome-actions"><span className="live-dot"><i/> Live overview</span>{user?.role !== 'cashier' && <Link to="/booking" className="primary-action"><FaPlus/> New Booking</Link>}</div>
    </section>

    {loading && <div className="refresh-note">Refreshing salon activity…</div>}

    <section className="metric-grid">{cards.map(({label,value,meta,icon:Icon,tone})=><article className="metric-card" key={label}><div className={`metric-icon ${tone}`}><Icon/></div><div><span>{label}</span><strong>{value}</strong><small>{meta}</small></div></article>)}</section>

    <section className="dash-grid-main">
      <div className="dash-column">
        <article className="panel revenue-panel">
          <div className="panel-heading"><div><span className="section-kicker">TODAY’S PERFORMANCE</span><h2>Daily revenue goal</h2></div><strong className="goal-percent">{progress.toFixed(0)}%</strong></div>
          <div className="goal-track"><span style={{width:`${progress}%`}}/></div>
          <div className="goal-stats"><div><span>Earned today</span><strong>{money(stats.netRevenue)}</strong></div><div><span>Daily target</span><strong>{money(DAILY_TARGET)}</strong></div><div><span>Average spend</span><strong>{money(stats.avgSpend)}</strong></div></div>
        </article>

        <article className="panel appointments-panel">
          <div className="panel-heading"><div><span className="section-kicker">FRONT DESK</span><h2>Today’s appointments</h2></div><Link to="/booking" className="text-link">View bookings <FaArrowRight/></Link></div>
          <div className="appointment-list">
            {liveBookings.length ? liveBookings.slice(0,6).map((b,i)=><div className="appointment-row" key={b.id || i}>
              <div className="appointment-time"><FaClock/><span>{b.time || b.booking_time || "Today"}</span></div>
              <div className="client-avatar">{(b.client_name || "G")[0].toUpperCase()}</div>
              <div className="appointment-main"><strong>{b.client_name || "Guest client"}</strong><span>{b.service || "Salon service"}</span></div>
              <div className="appointment-staff">{b.staff_name || "Unassigned"}</div>
              <span className={`booking-status ${(b.status || 'pending').toLowerCase().replace(/\s+/g,'-')}`}>{b.status || "Pending"}</span>
            </div>) : <div className="empty-state"><div className="empty-icon"><FaCalendarAlt/></div><strong>No appointments showing right now</strong><p>New and active bookings will appear here automatically.</p>{user?.role !== 'cashier' && <Link to="/booking">Open bookings</Link>}</div>}
          </div>
        </article>
      </div>

      <aside className="dash-column side-column">
        <article className="panel quick-panel"><div className="panel-heading"><div><span className="section-kicker">SHORTCUTS</span><h2>Quick actions</h2></div></div><div className="quick-grid">
          {user?.role !== 'cashier' && <Link to="/booking"><FaCalendarAlt/><span>New booking</span></Link>}
          {user?.role !== 'cashier' && <Link to="/staff"><FaUserPlus/><span>Manage staff</span></Link>}
          <Link to="/sales"><FaCashRegister/><span>Record sale</span></Link>
          <Link to="/products"><FaBoxOpen/><span>Inventory</span></Link>
        </div></article>

        <article className="panel inventory-panel"><div className="panel-heading"><div><span className="section-kicker">STOCK CONTROL</span><h2>Inventory alerts</h2></div><Link to="/products" className="text-link">View all</Link></div>
          <div className="inventory-list">{inventoryAlerts.length ? inventoryAlerts.slice(0,5).map((item,i)=><div className="inventory-row" key={item.id || i}><span className="alert-icon"><FaExclamationTriangle/></span><div><strong>{item.name || "Product"}</strong><span>Low stock</span></div><b>{item.stock ?? 0} left</b></div>) : <div className="healthy-stock"><span>✓</span><div><strong>Stock levels healthy</strong><p>No low-stock items need attention.</p></div></div>}</div>
        </article>
      </aside>
    </section>

    <section className="dash-grid-bottom">
      <article className="panel sales-panel"><div className="panel-heading"><div><span className="section-kicker">RECENT ACTIVITY</span><h2>Product sales</h2></div><Link to="/sales" className="text-link">Sales history <FaArrowRight/></Link></div>
        <div className="sales-list">{recentActivity.length ? recentActivity.slice(0,6).map((sale,i)=><div className="sale-row" key={sale.id || i}><span className="sale-index">{String(i+1).padStart(2,'0')}</span><div><strong>{sale.item_name || "Product sale"}</strong><span>{sale.time_ago || "Recently"}</span></div><b>{money(sale.amount)}</b></div>) : <div className="compact-empty">No recent product sales.</div>}</div>
      </article>
      <article className="panel salon-summary"><span className="section-kicker">SALON SNAPSHOT</span><h2>One place to run the day.</h2><p>Keep appointments, staff activity, sales and stock visible so the front desk can move faster and clients get a smoother experience.</p><div className="summary-links"><Link to="/booking"><FaCalendarAlt/> Bookings</Link><Link to="/staff"><FaCut/> Team</Link><Link to="/sales"><FaCashRegister/> Sales</Link></div></article>
    </section>
  </div>;
}
